import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BaseChartDirective } from 'ng2-charts';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CustomerStatsCardComponent } from '../../../components/customer-stats-card/customer-stats-card.component';
import { CampaignBottomCardComponent } from '../../../components/campaign-bottom-card/campaign-bottom-card.component';
import { DashboardStatData } from '../../../shared/types';
import { MobileDetectionMixin } from '../../../shared/mixins/mobile-detection.mixin';
import {
  DateRange,
  DateRangePickerComponent,
} from '../../../shared/components/date-range-picker/date-range-picker.component';

/**
 * Displays campaign-related dashboard metrics, charts and filter controls for the Campaign tab of the dashboard.
 * @component
 * @selector app-campaign-tab
 * @example
 *   <app-campaign-tab
 *     [selectedCampaignPeriod]="period"
 *     [campaignDashboardStats]="stats"
 *     [campaignPerformanceTrendData]="trendData"
 *     (selectCampaignPeriod)="onPeriodSelect($event)"
 *     (selectCampaignName)="onCampaignNameSelect($event)">
 *   </app-campaign-tab>
 * @input {string} selectedCampaignPeriod - Currently selected campaign period identifier or key.
 * @input {boolean} isCampaignDropdownOpen - Whether the campaign period dropdown is open.
 * @input {DashboardStatData[]} campaignDashboardStats - Top-level dashboard statistics for the campaign view.
 * @input {DashboardStatData[]} campaignBottomStats - Bottom card statistics shown for campaigns.
 * @input {Array<{value: string, label: string}>} periodOptions - Available period options for selection.
 * @input {string} selectedCampaignType - Currently selected campaign type identifier.
 * @input {boolean} isCampaignTypeDropdownOpen - Whether the campaign type dropdown is open.
 * @input {Array<{value: string, label: string}>} campaignTypeOptions - Options for campaign type selection.
 * @input {string} selectedCampaignName - Currently selected campaign name identifier.
 * @input {boolean} isCampaignNameDropdownOpen - Whether the campaign name dropdown is open.
 * @input {Array<{value: string, label: string}>} campaignNameOptions - Options for campaign name selection.
 * @input {any} campaignPerformanceTrendData - Data object for rendering the campaign performance trend chart.
 * @input {any} campaignPerformanceTrendOptions - Chart configuration/options for the trend chart.
 * @input {any} campaignFunnelData - Data object for rendering the campaign funnel chart.
 * @input {any} campaignFunnelOptions - Chart configuration/options for the funnel chart.
 * @input {string} selectedLocation - Currently selected location identifier (shared with other tabs).
 * @input {string} selectedLocationLabel - Display label for the selected location.
 * @input {boolean} isLocationDropdownOpen - Whether the location dropdown is open.
 * @input {Array<{value: string, label: string}>} locationOptions - Available location options.
 * @input {boolean} isInitialLoad - Flag indicating this is the initial page load (used for empty-state logic).
 * @input {boolean} hasAppliedFilters - Flag indicating whether filters have been applied (affects empty-state rules).
 * @output {EventEmitter<void>} toggleCampaignDropdown - Emitted to toggle the campaign period dropdown UI.
 * @output {EventEmitter<string>} selectCampaignPeriod - Emitted when a campaign period is selected (emits period id).
 * @output {EventEmitter<void>} toggleCampaignTypeDropdown - Emitted to toggle the campaign type dropdown UI.
 * @output {EventEmitter<string>} selectCampaignType - Emitted when a campaign type is selected (emits type id).
 * @output {EventEmitter<void>} toggleCampaignNameDropdown - Emitted to toggle the campaign name dropdown UI.
 * @output {EventEmitter<string>} selectCampaignName - Emitted when a campaign name is selected (emits campaign id).
 * @output {EventEmitter<void>} toggleLocationDropdown - Emitted to toggle the shared location dropdown UI.
 * @output {EventEmitter<string>} selectLocation - Emitted when a location is selected (emits location id).
 *
 * Key methods:
 * - isCampaignStatsDataEmpty(): boolean — Determines whether the main campaign stats array is effectively empty (all zero or absent).
 * - isCampaignBottomStatsDataEmpty(): boolean — Determines whether the bottom stats array is empty.
 * - isCampaignPerformanceTrendDataEmpty(): boolean — Checks whether trend chart data is null/empty or all zero.
 * - isCampaignFunnelDataEmpty(): boolean — Checks whether funnel chart data is null/empty or all zero.
 * - isAllDataEmpty(): boolean — Aggregates all data-empty checks to decide if the entire tab has no data.
 * - shouldShowEmptyState(): boolean — Helper that returns true when the Figma empty state should be shown (not initial load, no filters, and no data).
 * - shouldShowIndividualEmptyStates(): boolean — Helper used to decide whether to show per-chart empty placeholders (when filters applied or not initial load).
 * - displayCampaignName (getter): string — Computes and returns a human-readable label for the currently selected campaign name, falling back to a default prompt.
 *
 * Lifecycle and behavior notes:
 * - The component is driven primarily by @Input properties and emits user interactions via @Output events; it does not implement custom Angular lifecycle hooks itself in this file.
 * - Empty-state and visibility logic depend on isInitialLoad and hasAppliedFilters flags as well as the presence and content of provided data objects.
 * - Mobile and device detection behaviors are provided via the applied MobileDetectionMixin (not shown here), which may affect rendering or interactions.
 */
@Component({
  selector: 'app-campaign-tab',
  standalone: true,
  imports: [
    CommonModule,
    BaseChartDirective,
    MatTooltipModule,
    CustomerStatsCardComponent,
    CampaignBottomCardComponent,
    DateRangePickerComponent,
  ],
  templateUrl: './campaign-tab.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: '../../dashboard.component.scss',
})
export class CampaignTabComponent extends MobileDetectionMixin {
  @Input() selectedCampaignPeriod!: string;
  @Input() selectedPeriodLabel = '';
  @Input() isCampaignDropdownOpen = false;
  @Input() campaignDashboardStats: DashboardStatData[] = [];
  @Input() campaignBottomStats: DashboardStatData[] = [];
  @Input() periodOptions: { value: string; label: string }[] = [];
  @Input() customDateRange: DateRange = { startDate: null, endDate: null };

  @Input() selectedCampaignType!: string;
  @Input() isCampaignTypeDropdownOpen = false;
  @Input() campaignTypeOptions: { value: string; label: string }[] = [];

  @Input() selectedCampaignName!: string;
  @Input() isCampaignNameDropdownOpen = false;
  @Input() campaignNameOptions: { value: string; label: string }[] = [];

  @Input() campaignPerformanceTrendData: any;
  @Input() campaignPerformanceTrendOptions: any;
  @Input() campaignFunnelData: any;
  @Input() campaignFunnelOptions: any;

  // Location dropdown (shared with other tabs)
  @Input() selectedLocation = '';
  @Input() selectedLocationLabel = '';
  @Input() isLocationDropdownOpen = false;
  @Input() locationOptions: { value: string; label: string }[] = [];
  @Input() isInitialLoad = true; // Track if this is initial page load
  @Input() hasAppliedFilters = false; // Track if filters have been applied

  @Output() toggleCampaignDropdown = new EventEmitter<void>();
  @Output() selectCampaignPeriod = new EventEmitter<string>();
  @Output() dateRangeApply = new EventEmitter<{ period: string; customRange?: DateRange }>();
  @Output() closePeriodDropdown = new EventEmitter<void>();
  @Output() toggleCampaignTypeDropdown = new EventEmitter<void>();
  @Output() selectCampaignType = new EventEmitter<string>();
  @Output() toggleCampaignNameDropdown = new EventEmitter<void>();
  @Output() selectCampaignName = new EventEmitter<string>();

  @Output() toggleLocationDropdown = new EventEmitter<void>();
  @Output() selectLocation = new EventEmitter<string>();

  // Check if stats data is empty
  isCampaignStatsDataEmpty(): boolean {
    return (
      !this.campaignDashboardStats ||
      this.campaignDashboardStats.length === 0 ||
      this.campaignDashboardStats.every(
        stat => stat.value === '0' || parseInt(stat.value, 10) === 0
      )
    );
  }

  // Check if bottom stats data is empty
  isCampaignBottomStatsDataEmpty(): boolean {
    return (
      !this.campaignBottomStats ||
      this.campaignBottomStats.length === 0 ||
      this.campaignBottomStats.every(stat => stat.value === '0' || parseInt(stat.value, 10) === 0)
    );
  }

  // Check if campaign performance trend data is empty
  isCampaignPerformanceTrendDataEmpty(): boolean {
    // Return true if data is null, undefined, or empty
    if (!this.campaignPerformanceTrendData) {
      return true;
    }
    const data = this.campaignPerformanceTrendData?.datasets?.[0]?.data;
    return !data || data.length === 0 || data.every((value: number) => value === 0);
  }

  // Check if campaign funnel data is empty
  isCampaignFunnelDataEmpty(): boolean {
    // Return true if data is null, undefined, or empty
    if (!this.campaignFunnelData) {
      return true;
    }
    const data = this.campaignFunnelData?.datasets?.[0]?.data;
    return !data || data.length === 0 || data.every((value: number) => value === 0);
  }

  // Check if all data sources are empty
  isAllDataEmpty(): boolean {
    return (
      this.isCampaignStatsDataEmpty() &&
      this.isCampaignBottomStatsDataEmpty() &&
      this.isCampaignPerformanceTrendDataEmpty() &&
      this.isCampaignFunnelDataEmpty()
    );
  }

  // Helper method to determine if we should show the Figma empty state
  shouldShowEmptyState(): boolean {
    return !this.isInitialLoad && !this.hasAppliedFilters && this.isAllDataEmpty();
  }

  // Helper method to determine if we should show individual chart empty states
  shouldShowIndividualEmptyStates(): boolean {
    return this.hasAppliedFilters || !this.isInitialLoad;
  }

  // Getter to compute the campaign name label from the selected campaign name ID
  get displayCampaignName(): string {
    if (!this.selectedCampaignName) {
      return 'Select Campaign';
    }
    const match = this.campaignNameOptions.find(opt => opt.value === this.selectedCampaignName);
    return match ? match.label : this.selectedCampaignName;
  }

  trackByValue(_: number, option: { value: string }): string {
    return option.value;
  }
  trackByStatTitle(_: number, stat: DashboardStatData): string {
    return stat.title;
  }
}
